/**
 * HotkeyHelp — press "?" anywhere (when not in an input) to reveal this overlay.
 * Renderes as a frosted-glass panel with all available shortcuts listed.
 * Closes on Esc, click-outside, or pressing "?" again.
 */
import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Keyboard } from 'lucide-react'

interface ShortcutGroup {
  group: string
  items: { keys: string[]; label: string }[]
}

const SHORTCUTS: ShortcutGroup[] = [
  {
    group: 'Global',
    items: [
      { keys: ['⌘', 'K'], label: 'Open command palette' },
      { keys: ['?'],       label: 'Show / hide shortcuts' },
      { keys: ['Esc'],     label: 'Close modal / sheet' },
    ],
  },
  {
    group: 'Lead Pipeline',
    items: [
      { keys: ['⌘', 'N'],  label: 'Add new lead' },
      { keys: ['⌘', 'R'],  label: 'Refresh leads' },
    ],
  },
  {
    group: 'War Room',
    items: [
      { keys: ['⌘', 'R'],       label: 'Refresh floor data' },
      { keys: ['⌘', '⇧', 'B'], label: 'Open broadcast modal' },
    ],
  },
  {
    group: 'Supervisor Pages',
    items: [
      { keys: ['⌘', 'R'],  label: 'Refresh data (Leaderboard, Reports, Shifts)' },
    ],
  },
  {
    group: 'Agents',
    items: [
      { keys: ['⌘', 'N'],  label: 'Create new agent' },
      { keys: ['⌘', 'R'],  label: 'Refresh roster' },
    ],
  },
  {
    group: 'Dashboard',
    items: [
      { keys: ['⌘', 'R'],  label: 'Refresh metrics' },
    ],
  },
]

function KbdKey({ k }: { k: string }) {
  return (
    <kbd style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      minWidth: '1.625rem', height: '1.625rem', padding: '0 0.375rem',
      background: 'var(--bg-overlay)',
      border: '1px solid var(--border-default)',
      borderBottom: '2px solid var(--border-strong)',
      borderRadius: '0.375rem',
      fontFamily: 'var(--font-mono)', fontSize: '0.75rem',
      color: 'var(--text-primary)', lineHeight: 1,
      boxShadow: '0 1px 0 rgba(0,0,0,0.4)',
    }}>
      {k}
    </kbd>
  )
}

export default function HotkeyHelp() {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === '?' && !['INPUT', 'TEXTAREA', 'SELECT'].includes((e.target as HTMLElement)?.tagName)) {
        e.preventDefault()
        setOpen(o => !o)
      }
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [])

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            key="hotkey-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={() => setOpen(false)}
            style={{
              position: 'fixed', inset: 0, zIndex: 9998,
              background: 'rgba(5,3,8,0.65)', backdropFilter: 'blur(6px)',
            }}
          />

          {/* Panel */}
          <motion.div
            key="hotkey-panel"
            initial={{ opacity: 0, y: 20, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.97 }}
            transition={{ type: 'spring', stiffness: 350, damping: 28 }}
            style={{
              position: 'fixed', top: '50%', left: '50%', zIndex: 9999,
              transform: 'translate(-50%, -50%)',
              width: 'min(90vw, 38rem)',
              maxHeight: '80vh',
              overflowY: 'auto',
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border-default)',
              borderRadius: '1.25rem',
              boxShadow: 'var(--shadow-lg), var(--glow-sm)',
            }}
          >
            {/* Header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '1.25rem 1.5rem',
              borderBottom: '1px solid var(--border-subtle)',
              position: 'sticky', top: 0, zIndex: 1,
              background: 'var(--bg-elevated)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                <Keyboard size={16} style={{ color: 'var(--brand-glow)' }} />
                <span style={{ fontWeight: 700, fontSize: '0.9375rem' }}>Keyboard Shortcuts</span>
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close shortcuts"
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--text-muted)', display: 'flex', padding: '0.25rem',
                  borderRadius: '0.375rem', transition: 'color 150ms',
                }}
              >
                <X size={15} />
              </button>
            </div>

            {/* Groups */}
            <div style={{ padding: '1rem 1.5rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {SHORTCUTS.map(group => (
                <div key={group.group}>
                  <div style={{
                    fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em',
                    textTransform: 'uppercase', color: 'var(--text-muted)',
                    marginBottom: '0.625rem',
                  }}>
                    {group.group}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                    {group.items.map(item => (
                      <div key={item.label} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '0.5rem 0',
                        borderBottom: '1px solid var(--border-subtle)',
                      }}>
                        <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                          {item.label}
                        </span>
                        <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                          {item.keys.map((k, i) => <KbdKey key={i} k={k} />)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Footer hint */}
            <div style={{
              padding: '0.75rem 1.5rem',
              borderTop: '1px solid var(--border-subtle)',
              fontSize: '0.75rem', color: 'var(--text-muted)',
              textAlign: 'center',
            }}>
              Press <KbdKey k="?" /> or <KbdKey k="Esc" /> to dismiss
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
